import BadResponseError from '@/api/main/errors/BadResponseError';
import RequestNetworkProblemError from '@/api/main/errors/RequestNetworkProblemError';
import RequestTimeoutError from '@/api/main/errors/RequestTimeoutError';
import { GetLocationSuggestionsRequest } from '@/api/network/locationSuggestions/requests/GetLocationSuggestionsRequest';
import { LOCATION_FILTER_TYPES, } from '@/api/network/locationSuggestions/types/locationSuggestion';
const LOCATION_FILTER_TYPE_SET = new Set(LOCATION_FILTER_TYPES);
export class LocationSuggestionsError extends Error {
    constructor(code, message, status) {
        super(message);
        this.name = 'LocationSuggestionsError';
        this.code = code;
        this.status = status;
    }
}
function readFiniteNumber(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    return value;
}
function readText(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const normalized = value.trim();
    return normalized ? normalized : undefined;
}
function readCountryCode(value) {
    const normalized = readText(value);
    return normalized ? normalized.toUpperCase() : undefined;
}
function readProvinceCode(value) {
    const normalized = readText(value);
    return normalized ? normalized.toUpperCase() : undefined;
}
function readGeoPoint(value) {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const raw = value;
    const lat = readFiniteNumber(raw.lat);
    const lng = readFiniteNumber(raw.lng);
    if (lat == null || lng == null) {
        return undefined;
    }
    return { lat, lng };
}
function normalizeLocationType(value) {
    const normalized = typeof value === 'string' ? value.trim().toUpperCase() : '';
    if (normalized === 'PLACE') {
        return 'POINT';
    }
    if (normalized && LOCATION_FILTER_TYPE_SET.has(normalized)) {
        return normalized;
    }
    return 'POINT';
}
function normalizeBbox(value) {
    if (Array.isArray(value) && value.length >= 4) {
        const minLng = readFiniteNumber(value[0]);
        const minLat = readFiniteNumber(value[1]);
        const maxLng = readFiniteNumber(value[2]);
        const maxLat = readFiniteNumber(value[3]);
        if (minLng != null && minLat != null && maxLng != null && maxLat != null) {
            return { minLng, minLat, maxLng, maxLat };
        }
        return undefined;
    }
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const raw = value;
    const minLng = readFiniteNumber(raw.minLng);
    const minLat = readFiniteNumber(raw.minLat);
    const maxLng = readFiniteNumber(raw.maxLng);
    const maxLat = readFiniteNumber(raw.maxLat);
    if (minLng == null || minLat == null || maxLng == null || maxLat == null) {
        return undefined;
    }
    return { minLng, minLat, maxLng, maxLat };
}
function normalizeAddressPatch(value) {
    if (!value || typeof value !== 'object') {
        return undefined;
    }
    const raw = value;
    const patch = {
        countryCode: readCountryCode(raw.countryCode),
        region: readText(raw.region),
        province: readText(raw.province),
        provinceCode: readProvinceCode(raw.provinceCode),
        municipality: readText(raw.municipality),
        city: readText(raw.city),
        district: readText(raw.district),
        locality: readText(raw.locality),
        postalCode: readText(raw.postalCode),
        formattedAddress: readText(raw.formattedAddress),
        source: readText(raw.source),
        geo: readGeoPoint(raw.geo),
    };
    if (Object.values(patch).every((item) => item == null)) {
        return undefined;
    }
    return patch;
}
function normalizeSuggestion(item, index) {
    if (!item || typeof item !== 'object') {
        return null;
    }
    const raw = item;
    const addressPatch = normalizeAddressPatch(raw.addressPatch);
    const label = readText(raw.label)
        || readText(raw.name)
        || readText(raw.placeName)
        || readText(raw.text)
        || '';
    if (!label) {
        return null;
    }
    const geo = readGeoPoint(raw.geo);
    const lat = addressPatch?.geo?.lat
        ?? geo?.lat
        ?? readFiniteNumber(raw.lat)
        ?? readFiniteNumber(raw.latitude)
        ?? (Array.isArray(raw.center) ? readFiniteNumber(raw.center[1]) : null)
        ?? (raw.coordinates && typeof raw.coordinates === 'object'
            ? readFiniteNumber(raw.coordinates.lat)
            : null);
    const lng = addressPatch?.geo?.lng
        ?? geo?.lng
        ?? readFiniteNumber(raw.lng)
        ?? readFiniteNumber(raw.longitude)
        ?? (Array.isArray(raw.center) ? readFiniteNumber(raw.center[0]) : null)
        ?? (raw.coordinates && typeof raw.coordinates === 'object'
            ? readFiniteNumber(raw.coordinates.lng)
            : null);
    if (lat == null || lng == null) {
        return null;
    }
    const idCandidate = (typeof raw.id === 'string' && raw.id.trim())
        || (typeof raw.providerPlaceId === 'string' && raw.providerPlaceId.trim())
        || (typeof raw.placeId === 'string' && raw.placeId.trim())
        || (typeof raw.mapboxId === 'string' && raw.mapboxId.trim())
        || `${label}:${lat}:${lng}:${index}`;
    const legacyTypeRaw = readText(raw.type)?.toUpperCase();
    const legacyType = legacyTypeRaw === 'PLACE'
        ? 'PLACE'
        : (legacyTypeRaw && LOCATION_FILTER_TYPE_SET.has(legacyTypeRaw)
            ? legacyTypeRaw
            : undefined);
    return {
        id: idCandidate,
        label,
        locationType: normalizeLocationType(raw.locationType ?? raw.type ?? raw.kind),
        shortLabel: readText(raw.shortLabel),
        addressPatch,
        type: legacyType,
        municipality: addressPatch?.municipality ?? readText(raw.municipality),
        city: addressPatch?.city ?? readText(raw.city),
        district: addressPatch?.district ?? readText(raw.district),
        locality: addressPatch?.locality ?? readText(raw.locality),
        postalCode: addressPatch?.postalCode ?? readText(raw.postalCode) ?? readText(raw.postcode),
        province: addressPatch?.province ?? readText(raw.province),
        provinceCode: addressPatch?.provinceCode ?? readProvinceCode(raw.provinceCode),
        region: addressPatch?.region ?? readText(raw.region),
        countryCode: addressPatch?.countryCode ?? readCountryCode(raw.countryCode) ?? readCountryCode(raw.country),
        lat,
        lng,
        bbox: normalizeBbox(raw.bbox),
        provider: readText(raw.provider),
        providerPlaceId: readText(raw.providerPlaceId) ?? readText(raw.placeId),
    };
}
function normalizeSuggestionsPayload(payload) {
    const rawArray = Array.isArray(payload)
        ? payload
        : payload && typeof payload === 'object'
            ? ((Array.isArray(payload.items) && payload.items)
                || (Array.isArray(payload.suggestions) && payload.suggestions)
                || (Array.isArray(payload.results) && payload.results)
                || (Array.isArray(payload.data) && payload.data)
                || [])
            : [];
    return rawArray
        .map((item, index) => normalizeSuggestion(item, index))
        .filter((item) => item != null);
}
function toLocationSuggestionsError(error) {
    if (error instanceof BadResponseError) {
        const status = error.response.status;
        if (status === 400) {
            return new LocationSuggestionsError('BAD_REQUEST', 'Invalid location search request.', status);
        }
        if (status === 429) {
            return new LocationSuggestionsError('RATE_LIMIT', 'Too many requests.', status);
        }
        if (status >= 500) {
            return new LocationSuggestionsError('UNAVAILABLE', 'Location suggestions backend is unavailable.', status);
        }
        return new LocationSuggestionsError('UNKNOWN', `Location suggestions request failed with status ${status}.`, status);
    }
    if (error instanceof RequestTimeoutError || error instanceof RequestNetworkProblemError) {
        return new LocationSuggestionsError('NETWORK', 'Location suggestions request failed due to a network issue.');
    }
    return new LocationSuggestionsError('UNKNOWN', 'Unexpected location suggestions error.');
}
function isAbortError(error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }
    if (error instanceof RequestNetworkProblemError) {
        const original = error.originalError;
        return original?.name === 'AbortError';
    }
    return false;
}
export async function requestGetLocationSuggestions(params) {
    const query = params.q?.trim() ?? '';
    if (!query) {
        return [];
    }
    try {
        const request = new GetLocationSuggestionsRequest({
            ...params,
            q: query,
        });
        const payload = await request.send();
        return normalizeSuggestionsPayload(payload);
    }
    catch (error) {
        if (isAbortError(error)) {
            throw new DOMException('Aborted', 'AbortError');
        }
        throw toLocationSuggestionsError(error);
    }
}
